import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Gem, MapPin, Sparkles, Crown, Shield, Truck } from "lucide-react";
import { Input } from "@/components/ui/input";
import LeaseDealCard from "@/components/LeaseDealCard";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const EXOTIC_BRANDS = ["Lamborghini", "Ferrari", "Rolls-Royce", "Bentley", "McLaren", "Aston Martin", "Maserati", "Lotus", "Karma", "Lucid", "Porsche"];

export default function Exotic() {
  const [deals, setDeals] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", vehicle: "", budget: "",
    deal_type: "lease", location: "", income_range: "", credit_range: "",
    intended_use: "personal", timeline: "1_month", delivery: "enclosed", notes: "",
  });
  const [busy, setBusy] = useState(false);

  const openWithBrand = (brand) => {
    setForm((f) => ({ ...f, vehicle: f.vehicle && f.vehicle.toLowerCase().includes(brand.toLowerCase()) ? f.vehicle : brand + " " }));
    setOpen(true);
  };

  useEffect(() => {
    api.get("/lease-deals", { params: { is_exotic: true, limit: 60 } }).then((r) => setDeals(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/exotic-source-request", form);
      toast.success("Exotic desk notified. We'll be in touch within 24 hours.");
      setOpen(false);
      setForm({ ...form, name: "", email: "", phone: "", vehicle: "", budget: "", location: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't submit"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-gradient-to-b from-[#080608] via-[#050507] to-[#020203] min-h-screen" data-testid="exotic-page">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 md:py-24">
        <div className="flex items-center gap-2 mb-4">
          <Gem className="w-4 h-4 text-yellow-300" /><span className="label-mono text-yellow-200">Exotic · Ultra-Luxury · By Invitation</span>
        </div>
        <h1 className="font-display text-5xl md:text-7xl font-light tracking-tighter mb-5 bg-gradient-to-b from-white via-amber-100 to-amber-400 bg-clip-text text-transparent">
          Beyond the catalog.
        </h1>
        <p className="text-gray-400 max-w-2xl mb-4">Lamborghini, Ferrari, Rolls-Royce, Bentley, McLaren, Aston Martin and more. Allocations sourced through our dealer network, broker partners, and specialty leasing desks — not scraped from public sites.</p>
        <p className="text-[11px] text-yellow-200/70 max-w-2xl mb-10 flex items-center gap-2"><Shield className="w-3 h-3" /> Dealer-network sourced · Real allocations only · Click any brand below to start a quote request.</p>

        <div className="flex flex-wrap gap-2 mb-10">
          {EXOTIC_BRANDS.map((b) => (
            <button
              key={b}
              onClick={() => openWithBrand(b)}
              className="px-3 py-1.5 rounded-full border border-yellow-400/20 bg-yellow-400/5 hover:bg-yellow-400/15 hover:border-yellow-400/40 text-yellow-100 text-xs transition"
              data-testid={`exotic-brand-${b.toLowerCase().replace(/[^a-z]/g, "-")}`}
            >
              {b}
            </button>
          ))}
        </div>

        {/* Exotic deals OR empty state */}
        {deals.length === 0 ? (
          <div className="rounded-2xl border border-yellow-400/20 bg-gradient-to-b from-yellow-400/5 to-transparent p-10 text-center" data-testid="exotic-empty">
            <Crown className="w-10 h-10 mx-auto mb-4 text-yellow-300" strokeWidth={1.4} />
            <h2 className="font-display text-3xl mb-3">No verified exotic deal currently available.</h2>
            <p className="text-gray-400 max-w-xl mx-auto mb-7">Exotic and ultra-luxury allocations are tightly held. Tell us exactly what you want — our desk taps wholesalers, broker partners, and dealer contacts nationwide.</p>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button className="px-8 py-4 rounded-full bg-gradient-to-r from-yellow-300 to-amber-500 text-[#050507] font-bold tracking-wide" data-testid="exotic-source-cta">
                  Can't Find Your Dream Car? We'll Source It.
                </button>
              </DialogTrigger>
              <ExoticForm open={open} setOpen={setOpen} form={form} setForm={setForm} submit={submit} busy={busy} />
            </Dialog>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="exotic-deals">
              {deals.map((d) => <LeaseDealCard key={d.id} deal={d} />)}
            </div>
            <div className="mt-12 text-center">
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <button className="px-8 py-4 rounded-full bg-gradient-to-r from-yellow-300 to-amber-500 text-[#050507] font-bold tracking-wide" data-testid="exotic-source-cta">
                    Source A Specific Exotic
                  </button>
                </DialogTrigger>
                <ExoticForm open={open} setOpen={setOpen} form={form} setForm={setForm} submit={submit} busy={busy} />
              </Dialog>
            </div>
          </>
        )}

        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {[
            { icon: Shield, t: "Verified allocation only", d: "We confirm dealer allocation and VIN availability before quoting you." },
            { icon: Truck, t: "Enclosed transport included", d: "All exotics ship enclosed by default with white-glove delivery." },
            { icon: Crown, t: "Concierge close", d: "Document collection, registration, optional title-in-LLC for collectors." },
          ].map((b) => (
            <div key={b.t} className="rounded-2xl border border-yellow-400/10 bg-white/[0.02] p-6">
              <b.icon className="w-6 h-6 text-yellow-300 mb-3" strokeWidth={1.5} />
              <div className="font-display text-lg mb-2">{b.t}</div>
              <p className="text-sm text-gray-400 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>

        <p className="text-[11px] text-gray-500 mt-10 max-w-3xl leading-relaxed">
          Exotic and ultra-luxury lease offers are limited, may require higher credit qualifications, larger down payments, and are subject to dealer-specific terms and availability.
        </p>
      </div>
    </div>
  );
}

function ExoticForm({ open, setOpen, form, setForm, submit, busy }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <DialogContent className="bg-[#0A0A0E] border-yellow-400/20 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-display text-yellow-100">Source A Vehicle</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <Input placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#050507] border-[#1A1A24] h-11 text-white col-span-2" data-testid="exotic-form-name" />
        <Input placeholder="Email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[#050507] border-[#1A1A24] h-11 text-white" data-testid="exotic-form-email" />
        <Input placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[#050507] border-[#1A1A24] h-11 text-white" data-testid="exotic-form-phone" />
        <Input placeholder="Vehicle (e.g. Lamborghini Urus Performante)" value={form.vehicle} onChange={(e) => set("vehicle", e.target.value)} className="bg-[#050507] border-[#1A1A24] h-11 text-white col-span-2" data-testid="exotic-form-vehicle" />
        <Input placeholder="Budget ($/mo or cap cost)" value={form.budget} onChange={(e) => set("budget", e.target.value)} className="bg-[#050507] border-[#1A1A24] h-11 text-white" data-testid="exotic-form-budget" />
        <Input placeholder="Your city, state" value={form.location} onChange={(e) => set("location", e.target.value)} className="bg-[#050507] border-[#1A1A24] h-11 text-white" data-testid="exotic-form-location" />
        <select value={form.deal_type} onChange={(e) => set("deal_type", e.target.value)} className="bg-[#050507] border border-[#1A1A24] h-11 rounded-md text-white px-3 text-sm">
          <option value="lease">Lease</option><option value="finance">Finance</option><option value="cash">Cash</option>
        </select>
        <select value={form.intended_use} onChange={(e) => set("intended_use", e.target.value)} className="bg-[#050507] border border-[#1A1A24] h-11 rounded-md text-white px-3 text-sm">
          <option value="personal">Personal</option><option value="business">Business</option><option value="exotic_rental">Exotic Rental</option><option value="collection">Collection</option>
        </select>
        <select value={form.income_range} onChange={(e) => set("income_range", e.target.value)} className="bg-[#050507] border border-[#1A1A24] h-11 rounded-md text-white px-3 text-sm">
          <option value="">Income range…</option><option value="200k_500k">$200k–$500k</option><option value="500k_1m">$500k–$1M</option><option value="1m_plus">$1M+</option>
        </select>
        <select value={form.credit_range} onChange={(e) => set("credit_range", e.target.value)} className="bg-[#050507] border border-[#1A1A24] h-11 rounded-md text-white px-3 text-sm">
          <option value="">Credit range…</option><option value="excellent">Excellent (740+)</option><option value="good">Good (680–739)</option><option value="other">Other</option>
        </select>
        <select value={form.timeline} onChange={(e) => set("timeline", e.target.value)} className="bg-[#050507] border border-[#1A1A24] h-11 rounded-md text-white px-3 text-sm">
          <option value="asap">ASAP</option><option value="1_month">Within a month</option><option value="3_months">1–3 months</option><option value="exploring">Exploring</option>
        </select>
        <select value={form.delivery} onChange={(e) => set("delivery", e.target.value)} className="bg-[#050507] border border-[#1A1A24] h-11 rounded-md text-white px-3 text-sm">
          <option value="enclosed">Enclosed transport (recommended)</option><option value="open">Open transport</option><option value="pickup">I'll pick up</option>
        </select>
        <textarea rows={3} placeholder="Specific spec / color / options" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="col-span-2 w-full bg-[#050507] border border-[#1A1A24] rounded-md text-white p-3 text-sm" />
      </div>
      <button onClick={submit} disabled={busy} className="mt-4 w-full bg-gradient-to-r from-yellow-300 to-amber-500 text-[#050507] py-3 rounded-full font-bold disabled:opacity-50" data-testid="exotic-form-submit">
        {busy ? "Sending…" : "Submit to Exotic Desk"}
      </button>
    </DialogContent>
  );
}
